import React, { ReactElement } from "react";

import Profile from "components/Profile";
import { useIsInEditMode } from "hooks";
import { BodyL } from "style/components/Typography";
import { addEditAttributes } from "utils/episerver";

import {
    Container,
    Heading,
    PreHeading,
    Introduction,
    Content,
    Authors,
    IntroductionHtml,
} from "./NewsPageSection.styled";
import NewsPageSectionProps from "./NewsPageSectionProps";

const NewsPageSection = ({
    preHeading,
    heading,
    introduction,
    introductionHtml,
    mainBody,
    author,
    identifier,
    theme,
    contentTheme,
    actionTheme,
    anchorName,
    l18n,
}: NewsPageSectionProps): ReactElement => {
    const isInEditMode = useIsInEditMode();
    const authors = (Array.isArray(author) ? author : [author]).filter(Boolean); // Author can be an array or a single object
    return (
        <Container key={identifier} id={anchorName || identifier}>
            {(isInEditMode || preHeading) && (
                <PreHeading {...addEditAttributes("PreHeading")}>
                    {preHeading}
                </PreHeading>
            )}
            {(isInEditMode || heading) && (
                <Heading {...addEditAttributes("Heading")}>{heading}</Heading>
            )}
            {(isInEditMode || (!introductionHtml && introduction)) && (
                <Introduction
                    {...addEditAttributes(`Introduction`)}
                    dangerouslySetInnerHTML={{
                        __html: introduction || "",
                    }}
                />
            )}
            {(isInEditMode || introductionHtml) && (
                <IntroductionHtml
                    editPropertyName={"IntroductionHtml"}
                    content={introductionHtml}
                    renderElement={<BodyL />}
                />
            )}
            {(isInEditMode || (authors && authors.length > 0)) && (
                <Authors>
                    {authors.map((author, index) => (
                        <>
                            {author && (
                                <Profile
                                    key={`Author${index}`}
                                    fullName={author.fullName}
                                    initials={author.initials}
                                    imageUrl={author.imageUrl}
                                    role={author.role}
                                    url={author.url}
                                    businessArea={author.businessArea}
                                    size="small"
                                    editPropertyName="Author"
                                />
                            )}
                        </>
                    ))}
                </Authors>
            )}
            {(isInEditMode || mainBody) && (
                <Content
                    content={mainBody}
                    areaName="mainBody"
                    theme={theme}
                    contentTheme={contentTheme}
                    actionTheme={actionTheme}
                    editPropertyName="MainBody"
                    editorialGrid={true}
                    lightbox={true}
                    l18n={l18n}
                />
            )}
        </Container>
    );
};

export default React.memo(NewsPageSection);
